import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calculator, ChevronDown, ChevronUp, Plus, Settings, X } from 'lucide-react';
import { updatePageMeta, updateJsonLd, generateSimulatorSchema } from '../../utils/seoUtils';
import { trackPageView } from '../../utils/googleAnalytics';
import {
  trackSimulatorStarted,
  trackSimulatorCompleted,
  trackSimulatorPeriodSwitched,
} from '../../utils/analyticsEvents';
import { useAppContext } from '../../store';
import { useToast } from '../../hooks/useToast';

import { useSimulatorState } from './hooks/useSimulatorState';
import { useSimulatorCalculations } from './hooks/useSimulatorCalculations';
import { useSimulatorPersistence } from './hooks/useSimulatorPersistence';

import ActSelector from './components/ActSelector';
import SimulatorCartTable from './components/SimulatorCartTable';
import PeriodAndMultiplierSettings from './components/PeriodAndMultiplierSettings';
import AppinesBanner from '../../components/AppinesBanner';
import RevenueChart from './components/RevenueChart';
import Footer from '../../components/Footer';

const SimulatorPage: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useAppContext();
  const { darkMode } = state.ui;
  
  const [isParametersExpanded, setIsParametersExpanded] = React.useState(false);
  const [isActSelectorExpanded, setIsActSelectorExpanded] = React.useState(true);
  const [isCartExpanded, setIsCartExpanded] = React.useState(true);
  const [isChartExpanded, setIsChartExpanded] = React.useState(true);

  const [isMobileActSelectorOpen, setIsMobileActSelectorOpen] = React.useState(false);
  const [isMobileSettingsOpen, setIsMobileSettingsOpen] = React.useState(false);

  useEffect(() => {
    if (!isMobileActSelectorOpen && !isMobileSettingsOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMobileActSelectorOpen(false);
        setIsMobileSettingsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isMobileActSelectorOpen, isMobileSettingsOpen]);
  
  const { success } = useToast();

  const {
    selectedPeriod,
    selectedActId,
    isDrom,
    customMultipliers,
    customPointValues,
    addToCart,
    removeFromCart,
    updateQuantity,
    updateHomeVisitPercentage,
    updateBalneotherapy,
    clearCart,
    setSelectedPeriod,
    setSelectedActId,
    setIsDrom,
    updateCustomMultiplier,
    updateCustomPointValue
  } = useSimulatorState();

  const { summary, itemEvolutions } = useSimulatorCalculations(
    state.searchAndFilter.cartItems,
    selectedPeriod,
    isDrom,
    customMultipliers,
    customPointValues
  );

  useSimulatorPersistence({
    cartItems: state.searchAndFilter.cartItems,
    selectedPeriod,
    isDrom,
    customMultipliers,
    customPointValues
  });

  useEffect(() => {
    updatePageMeta(undefined, {
      title: 'Simulateur de Revenus NGAP - Calculez vos gains kinésithérapie',
      description: 'Simulez vos revenus de kinésithérapeute avec les cotations NGAP actuelles et futures. Calculez l\'impact des revalorisations 2025-2027 sur votre activité.',
      keywords: 'simulateur revenus, NGAP, kinésithérapie, revalorisation, calcul tarifs, gains kinésithérapeute',
      canonical: 'https://ngap-kine.fr/simulateur'
    });

    const jsonLdSchema = generateSimulatorSchema();
    updateJsonLd(jsonLdSchema, 'simulator-page-jsonld');

    trackPageView('Simulateur de Revenus', '/simulateur');
    trackSimulatorStarted();
  }, []);

  const previousPeriodRef = React.useRef(selectedPeriod);
  useEffect(() => {
    if (previousPeriodRef.current !== selectedPeriod) {
      trackSimulatorPeriodSwitched(selectedPeriod);
      previousPeriodRef.current = selectedPeriod;
    }
  }, [selectedPeriod]);

  const simulatorCompletedRef = React.useRef(false);
  useEffect(() => {
    const revenue = summary?.totalCurrentRevenue ?? 0;
    const hasItems = state.searchAndFilter.cartItems.length > 0;
    if (!simulatorCompletedRef.current && hasItems && revenue > 0) {
      simulatorCompletedRef.current = true;
      trackSimulatorCompleted(revenue);
    }
    if (!hasItems) {
      simulatorCompletedRef.current = false;
    }
  }, [summary, state.searchAndFilter.cartItems.length]);

  const handleValidateParameters = React.useCallback(() => {
    success(
      'Paramètres appliqués',
      'Les nouveaux paramètres de calcul ont été pris en compte'
    );
    setIsParametersExpanded(false);
    setIsMobileSettingsOpen(false);
  }, [success, customPointValues]);

  const handleMobileAddToCart = React.useCallback(
    (...args: Parameters<typeof addToCart>) => {
      addToCart(...args);
      setIsMobileActSelectorOpen(false);
    },
    [addToCart]
  );

  const sectionCard = `rounded-lg border ${
    darkMode ? 'bg-appines-petrol border-appines-white/12' : 'bg-appines-white border-[rgba(1,42,54,0.12)]'
  }`;
  const sectionBtn = `w-full flex items-center justify-between p-6 text-left transition-colors ${
    darkMode ? 'hover:bg-appines-white/5' : 'hover:bg-[rgba(1,42,54,0.04)]'
  }`;
  const sectionTitle = `text-lg font-semibold ${darkMode ? 'text-appines-white' : 'text-appines-petrol'}`;
  const chevronColor = darkMode ? 'text-appines-white/45' : 'text-appines-petrol/45';

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      darkMode ? 'bg-appines-petrol' : 'bg-appines-white'
    }`}>
      <AppinesBanner className="md:pt-6" />
      
      <div className="pt-20 md:pt-0 pb-40 md:pb-0">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="flex items-start mb-6 md:mb-8 gap-2">
            <button
              onClick={() => navigate('/')}
              className={`mt-1 p-2 rounded-lg transition-colors ${
                darkMode
                  ? 'text-appines-white/65 hover:text-appines-white hover:bg-appines-white/10'
                  : 'text-appines-petrol/65 hover:text-appines-petrol hover:bg-[rgba(1,42,54,0.06)]'
              }`}
              aria-label="Retour à l'accueil"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className={`text-2xl md:text-4xl font-bold flex items-center ${
                darkMode ? 'text-appines-white' : 'text-appines-petrol'
              }`}>
                <Calculator className="h-6 w-6 md:h-8 md:w-8 mr-2 md:mr-3 text-appines-emerald shrink-0" />
                <span className="truncate">Simulateur de Revenus NGAP</span>
              </h1>
              <p className={`text-sm md:text-lg mt-1 md:mt-2 ${
                darkMode ? 'text-appines-white/65' : 'text-appines-petrol/65'
              }`}>
                Calculez l'impact des revalorisations sur vos revenus de kinésithérapeute
              </p>
            </div>
            <button
              onClick={() => setIsMobileSettingsOpen(true)}
              className={`md:hidden mt-1 p-2 rounded-lg transition-colors ${
                darkMode
                  ? 'text-appines-white/65 hover:text-appines-white hover:bg-appines-white/10'
                  : 'text-appines-petrol/65 hover:text-appines-petrol hover:bg-[rgba(1,42,54,0.06)]'
              }`}
              aria-label="Paramètres de calcul"
              title="Paramètres de calcul"
            >
              <Settings className="h-5 w-5" />
            </button>
          </div>

          <div className="w-full space-y-6">
                {/* Parametres */}
                <div className={`hidden md:block ${sectionCard}`}>
                  <button onClick={() => setIsParametersExpanded(!isParametersExpanded)} className={sectionBtn}>
                    <h3 className={sectionTitle}>Paramètres de calcul</h3>
                    {isParametersExpanded ? <ChevronUp className={`w-5 h-5 ${chevronColor}`} /> : <ChevronDown className={`w-5 h-5 ${chevronColor}`} />}
                  </button>
                  {isParametersExpanded && (
                    <div className="px-6 pb-6">
                      <PeriodAndMultiplierSettings
                        selectedPeriod={selectedPeriod} onPeriodChange={setSelectedPeriod}
                        isDrom={isDrom} onDromChange={setIsDrom}
                        customMultipliers={customMultipliers} onMultiplierChange={updateCustomMultiplier}
                        customPointValues={customPointValues} onPointValueChange={updateCustomPointValue}
                        darkMode={darkMode} onValidateChanges={handleValidateParameters}
                      />
                    </div>
                  )}
                </div>

                {/* Act selector */}
                <div className={`hidden md:block ${sectionCard}`}>
                  <button onClick={() => setIsActSelectorExpanded(!isActSelectorExpanded)} className={sectionBtn}>
                    <h3 className={sectionTitle}>Ajouter des actes</h3>
                    {isActSelectorExpanded ? <ChevronUp className={`w-5 h-5 ${chevronColor}`} /> : <ChevronDown className={`w-5 h-5 ${chevronColor}`} />}
                  </button>
                  {isActSelectorExpanded && (
                    <div className="px-6 pb-6">
                      <ActSelector onAddToCart={addToCart} selectedActId={selectedActId} onSelectAct={setSelectedActId} darkMode={darkMode} />
                    </div>
                  )}
                </div>
                
                {/* Cart */}
                <div className={sectionCard}>
                  <button onClick={() => setIsCartExpanded(!isCartExpanded)} className={sectionBtn}>
                    <div className="flex items-center">
                      <h3 className={sectionTitle}>Actes sélectionnés</h3>
                      {state.searchAndFilter.cartItems.length > 0 && (
                        <span className="ml-2 px-2 py-1 text-xs rounded-full bg-appines-glass/25 text-appines-petrol">
                          {state.searchAndFilter.cartItems.length}
                        </span>
                      )}
                    </div>
                    {isCartExpanded ? <ChevronUp className={`w-5 h-5 ${chevronColor}`} /> : <ChevronDown className={`w-5 h-5 ${chevronColor}`} />}
                  </button>
                  {isCartExpanded && (
                    <div className="px-6 pb-6">
                      <SimulatorCartTable
                        key={`cart-${JSON.stringify(customMultipliers)}-${JSON.stringify(customPointValues)}-${isDrom}-${selectedPeriod}`}
                        cartItems={state.searchAndFilter.cartItems} selectedPeriod={selectedPeriod}
                        itemEvolutions={itemEvolutions} summary={summary}
                        onUpdateQuantity={updateQuantity} onUpdateHomeVisitPercentage={updateHomeVisitPercentage}
                        onUpdateBalneotherapy={updateBalneotherapy} onRemoveFromCart={removeFromCart}
                        onClearCart={clearCart} onPeriodChange={setSelectedPeriod} darkMode={darkMode}
                      />
                    </div>
                  )}
                </div>
                
                {/* Chart */}
                {state.searchAndFilter.cartItems.length > 0 && (
                  <div className={sectionCard}>
                    <button onClick={() => setIsChartExpanded(!isChartExpanded)} className={sectionBtn}>
                      <div className="flex items-center">
                        <h3 className={sectionTitle}>Visualisation graphique</h3>
                        <span className="ml-2 px-2 py-1 text-xs rounded-full bg-appines-glass/25 text-appines-petrol">
                          Nouveau
                        </span>
                      </div>
                      {isChartExpanded ? <ChevronUp className={`w-5 h-5 ${chevronColor}`} /> : <ChevronDown className={`w-5 h-5 ${chevronColor}`} />}
                    </button>
                    {isChartExpanded && (
                      <div className="px-6 pb-6">
                        <RevenueChart cartItems={state.searchAndFilter.cartItems} itemEvolutions={itemEvolutions} selectedPeriod={selectedPeriod} isDrom={isDrom} darkMode={darkMode} />
                      </div>
                    )}
                  </div>
                )}
          </div>
        </div>

        <Footer />
      </div>

      {/* Mobile sticky bar */}
      <div
        className={`md:hidden fixed bottom-0 inset-x-0 z-40 border-t backdrop-blur-md ${
          darkMode ? 'bg-appines-petrol/95 border-appines-white/12' : 'bg-appines-white/95 border-[rgba(1,42,54,0.12)]'
        }`}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0) + 0.75rem)' }}
      >
        {state.searchAndFilter.cartItems.length > 0 && summary && (
          <div className={`px-4 pt-2.5 pb-2 border-b ${darkMode ? 'border-appines-white/12' : 'border-[rgba(1,42,54,0.08)]'}`} aria-live="polite">
            <div className="flex items-center justify-between gap-2 text-sm">
              <div className={`shrink-0 text-[10px] leading-tight font-medium ${darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65'}`}>
                {summary.itemCount} acte{summary.itemCount > 1 ? 's' : ''}
                <span className="mx-1">·</span>
                {summary.totalQuantityInSelectedPeriod}/{({ day: 'j', week: 'sem', month: 'mois', year: 'an' } as const)[selectedPeriod]}
              </div>
              <div className="flex flex-col items-end leading-tight">
                <span className={`text-[9px] uppercase tracking-wide font-medium ${darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65'}`}>Actuel</span>
                <span className={`text-sm font-bold ${darkMode ? 'text-appines-white' : 'text-appines-black'}`}>{summary.totalCurrentRevenue.toFixed(0)}€</span>
              </div>
              <div className="flex flex-col items-end leading-tight">
                <span className={`text-[9px] uppercase tracking-wide font-medium ${darkMode ? 'text-appines-white/65' : 'text-appines-petrol'}`}>2026</span>
                <span className={`text-sm font-bold ${
                  summary.totalFutureRevenueSept2026 > summary.totalCurrentRevenue ? 'text-appines-emerald'
                    : summary.totalFutureRevenueSept2026 < summary.totalCurrentRevenue ? 'text-[#c04040]'
                    : darkMode ? 'text-appines-white' : 'text-appines-black'
                }`}>{summary.totalFutureRevenueSept2026.toFixed(0)}€</span>
              </div>
              <div className="flex flex-col items-end leading-tight">
                <span className={`text-[9px] uppercase tracking-wide font-medium ${darkMode ? 'text-appines-white/65' : 'text-appines-petrol'}`}>2027</span>
                <span className={`text-sm font-bold ${
                  summary.totalDifference2027 > 0 ? 'text-appines-emerald'
                    : summary.totalDifference2027 < 0 ? 'text-[#c04040]'
                    : darkMode ? 'text-appines-white' : 'text-appines-black'
                }`}>{summary.totalFutureRevenue2027.toFixed(0)}€</span>
              </div>
            </div>
          </div>
        )}
        <div className="px-4 pt-3">
          <button
            onClick={() => setIsMobileActSelectorOpen(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-appines-petrol bg-appines-emerald hover:bg-[#22b886] active:bg-[#1a9e72] shadow-lg transition-colors"
            aria-label="Ajouter un acte"
          >
            <Plus className="h-5 w-5" />
            Ajouter un acte
          </button>
        </div>
      </div>

      {/* Mobile drawer: ActSelector */}
      {isMobileActSelectorOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex items-end" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-appines-black/50" onClick={() => setIsMobileActSelectorOpen(false)} />
          <div className={`relative w-full max-h-[90vh] rounded-t-2xl shadow-2xl flex flex-col ${darkMode ? 'bg-appines-petrol' : 'bg-appines-white'}`} style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}>
            <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-appines-white/12' : 'border-[rgba(1,42,54,0.12)]'}`}>
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-appines-white' : 'text-appines-petrol'}`}>Ajouter un acte</h3>
              <button onClick={() => setIsMobileActSelectorOpen(false)} className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-appines-white/65 hover:text-appines-white hover:bg-appines-white/10' : 'text-appines-petrol/65 hover:text-appines-petrol hover:bg-[rgba(1,42,54,0.06)]'}`} aria-label="Fermer">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-4 flex-1">
              <ActSelector onAddToCart={handleMobileAddToCart} selectedActId={selectedActId} onSelectAct={setSelectedActId} darkMode={darkMode} />
            </div>
          </div>
        </div>
      )}

      {/* Mobile drawer: Settings */}
      {isMobileSettingsOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex items-end" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-appines-black/50" onClick={() => setIsMobileSettingsOpen(false)} />
          <div className={`relative w-full max-h-[90vh] rounded-t-2xl shadow-2xl flex flex-col ${darkMode ? 'bg-appines-petrol' : 'bg-appines-white'}`} style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}>
            <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-appines-white/12' : 'border-[rgba(1,42,54,0.12)]'}`}>
              <h3 className={`text-lg font-semibold flex items-center gap-2 ${darkMode ? 'text-appines-white' : 'text-appines-petrol'}`}>
                <Settings className="h-5 w-5" />
                Paramètres de calcul
              </h3>
              <button onClick={() => setIsMobileSettingsOpen(false)} className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-appines-white/65 hover:text-appines-white hover:bg-appines-white/10' : 'text-appines-petrol/65 hover:text-appines-petrol hover:bg-[rgba(1,42,54,0.06)]'}`} aria-label="Fermer">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-4 flex-1">
              <PeriodAndMultiplierSettings
                selectedPeriod={selectedPeriod} onPeriodChange={setSelectedPeriod}
                isDrom={isDrom} onDromChange={setIsDrom}
                customMultipliers={customMultipliers} onMultiplierChange={updateCustomMultiplier}
                customPointValues={customPointValues} onPointValueChange={updateCustomPointValue}
                darkMode={darkMode} onValidateChanges={handleValidateParameters}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimulatorPage;
